import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Ticket, MessageCircle, CheckCircle, XCircle, Clock, Send, Upload, Download, Wifi, WifiOff, ChevronDown, ChevronUp, FileText, Building2, ArrowLeftRight, HelpCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import axiosInstance from '../services/axiosInstance';
import { isAuthenticated, getAuthHeader, getAssociateData, getToken } from '../services/otpAuthService';
import { useVoucherSocket } from '../hooks/useVoucherSocket';
import { useNotifications } from '../hooks/useNotifications';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';
import { valutaConfermaScambio } from '../utils/voucherConferma';

interface Matricola {
  _id: string;
  code: string;
  agenzia: string;
  provincia: string;
  motorizzazione: string;
  utenza: string;
}

interface STACodeInfo {
  _id: string;
  staCode: string;
  provincia: string;
  matricole: Matricola[];
}

interface Voucher {
  _id: string;
  tariffario: string;
  pratica: {
    categoria: string;
    catalogo: string;
    descrizionePratica: string;
    codicePratica: string;
  };
  tariffe?: {
    diritti?: number;
    bolli1?: number;
    bolli2?: number;
    costo?: number;
    diritti2?: number;
    diritti3?: number;
    diritti4?: number;
    gas?: number;
    contributi?: number;
    dirittiSted?: number;
    corrispettivoSted?: number;
    tributiSpeciali?: number;
    rimborsoMissioni?: number;
    esenzione?: boolean;
    cumulabile?: boolean;
  };
  causale: string;
  quantita: number;
  isCumulativo?: boolean;
  numeroPratiche?: number;
  stato: 'disponibile' | 'prenotato' | 'completato' | 'annullato' | 'scaduto';
  proprietario: {
    _id: string;
    nominativo: string;
    rappresentante?: string;
    codice: string;
    email: string;
    telefono?: string;
    citta?: string;
    provincia?: string;
    staCodes?: STACodeInfo[];
  };
  prenotante?: {
    _id: string;
    nominativo: string;
    rappresentante?: string;
    codice: string;
    email: string;
    telefono?: string;
    citta?: string;
    staCodes?: STACodeInfo[];
    provincia?: string;
  };
  dataPrenotazione?: string;
  scadenzaPrenotazione?: string;
  dataCompletamento?: string;
  conferme?: {
    proprietarioConfermato: boolean;
    prenotatoConfermato: boolean;
    dataConfermaProprietario?: string;
    dataConfermaPrenotante?: string;
  };
  note?: string;
  motivoAnnullamento?: string;
  isMessaggisticaAperta?: boolean;
  interventoAdminRichiesto?: boolean;
  dataRichiestaInterventoAdmin?: string;
  adminInChat?: boolean;
  dataIngressoAdmin?: string;
  dataUscitaAdmin?: string;
  voucherPDF?: {
    filename: string;
    uploadedAt: string;
  };
  ricevutaPagamentoPDF?: {
    filename: string;
    uploadedAt: string;
  };
  createdAt: string;
}

interface Messaggio {
  _id: string;
  mittente?: {
    _id: string;
    nominativo: string;
    rappresentante?: string;
    codice: string;
  };
  mittenteAdmin?: {
    _id: string;
    name: string;
  };
  mittenteAdminNome?: string;
  destinatario: {
    _id: string;
    nominativo: string;
    rappresentante?: string;
    codice: string;
  };
  testo: string;
  tipo?: 'messaggio' | 'sistema';
  letto: boolean;
  createdAt: string;
}

// Helper per verificare se l'utente è admin (JWT) o associato (OTP)
const isAdminUser = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

const RimborsoVoucherDettaglioPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [messaggi, setMessaggi] = useState<Messaggio[]>([]);
  const [nuovoMessaggio, setNuovoMessaggio] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Proprietario che dichiara di aver ricevuto il rimborso in altro modo (senza ricevuta caricata)
  const [confermaSenzaRicevuta, setConfermaSenzaRicevuta] = useState(false);
  const [showAnnullaModal, setShowAnnullaModal] = useState(false);
  const [motivoAnnullamento, setMotivoAnnullamento] = useState('');
  const [uploadingVoucher, setUploadingVoucher] = useState(false);
  const [uploadingRicevuta, setUploadingRicevuta] = useState(false);
  const [, setRichiedendoIntervento] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [entrandoInChat, setEntrandoInChat] = useState(false);
  const [uscendoDaChat, setUscendoDaChat] = useState(false);
  const [showDettagliTu, setShowDettagliTu] = useState(false);
  const [showDettagliControparte, setShowDettagliControparte] = useState(false);

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('voucher_dettaglio_tour');

  const tourSteps = [
    {
      target: '#tour-vdettaglio-stato',
      content: 'Qui vedi a che punto è lo scambio e cosa devi fare ora. Ordine: prenotazione → PDF voucher (proprietario) → pagamento e ricevuta (prenotante) → conferme di entrambi. Il bottone "Conferma Scambio" compare solo quando tocca a te.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-vdettaglio-pratica',
      content: 'Dettagli della pratica: codice, descrizione e tariffe applicate.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-vdettaglio-contatti',
      content: 'Le due parti dello scambio. Il badge sotto ogni nominativo indica se quella parte ha già confermato: "Confermato" in verde, altrimenti cosa sta aspettando.',
      placement: 'bottom' as const
    },
    ...(document.getElementById('tour-vdettaglio-messaggi') ? [{
      target: '#tour-vdettaglio-messaggi',
      content: 'Usa la chat per comunicare con l\'altro studio sulla gestione del voucher.',
      placement: 'top' as const
    }] : []),
    ...(document.getElementById('tour-vdettaglio-upload-voucher') ? [{
      target: '#tour-vdettaglio-upload-voucher',
      content: 'Il proprietario carica qui il PDF del voucher PagoPA. Dopo il caricamento il prenotante può scaricarlo e confermarne la ricezione.',
      placement: 'top' as const
    }] : []),
    ...(document.getElementById('tour-vdettaglio-upload-ricevuta') ? [{
      target: '#tour-vdettaglio-upload-ricevuta',
      content: 'Il prenotante, dopo aver pagato il proprietario (es. bonifico), carica qui la ricevuta. Solo allora il proprietario potrà confermare di aver ricevuto il rimborso.',
      placement: 'top' as const
    }] : [])
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const associateData = getAssociateData();
  const currentAssociateId = associateData?.id;

  // Determina se abilitare socket (solo per voucher prenotato/completato con messaggistica aperta)
  const socketEnabled = !!(
    id &&
    voucher &&
    (voucher.stato === 'prenotato' || voucher.stato === 'completato')
  );

  // Hook socket per messaggistica real-time
  const handleNewMessage = useCallback((msg: any) => {
    // Converti formato socket al formato Messaggio
    const newMsg: Messaggio = {
      _id: msg.id,
      mittente: msg.mittente,
      mittenteAdmin: msg.mittenteAdmin ? { _id: msg.mittenteAdmin, name: msg.mittenteAdminNome || 'Admin' } : undefined,
      mittenteAdminNome: msg.mittenteAdminNome,
      destinatario: msg.destinatario,
      testo: msg.testo,
      tipo: msg.tipo,
      letto: false,
      createdAt: msg.createdAt
    };
    setMessaggi(prev => [...prev, newMsg]);
  }, []);

  // Handler per aggiornamenti voucher in tempo reale (upload PDF, conferma)
  const handleVoucherUpdated = (event: { voucherId: string; type: string; message: string }) => {
    console.log('📡 Voucher aggiornato via socket:', event.type);
    loadVoucher();
  };

  const {
    connected: socketConnected,
    onlineUsers,
    typingUsers,
    sendMessage: sendSocketMessage,
    startTyping,
    markAsRead
  } = useVoucherSocket({
    voucherId: id || '',
    onNewMessage: handleNewMessage,
    onVoucherUpdated: handleVoucherUpdated,
    enabled: socketEnabled
  });

  useEffect(() => {
    // Verifica autenticazione: admin (JWT) o associato (OTP)
    const isAdminAuthenticated = isAdminUser();
    const isAssociateAuthenticated = isAuthenticated();

    if (!isAdminAuthenticated && !isAssociateAuthenticated) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }

    loadVoucher();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [id, navigate]);

  // Hook notifiche unificate (solo per associati, non admin)
  const { markVoucherAsRead } = useNotifications({ enabled: false, autoFetch: false });

  // Carica messaggi iniziali quando il voucher è disponibile (solo una volta)
  useEffect(() => {
    if (voucher && (voucher.stato === 'prenotato' || voucher.stato === 'completato')) {
      loadMessaggi();
      // Marca messaggi come letti via socket
      if (socketConnected) {
        markAsRead();
      }
      // Marca notifiche unificate come lette (solo associati)
      if (!isAdminUser() && voucher._id) {
        markVoucherAsRead(voucher._id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [voucher?._id, socketConnected]);

  // Scroll alla fine della chat quando arrivano nuovi messaggi
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messaggi]);

  const loadVoucher = async () => {
    try {
      setLoading(true);
      let response;

      if (isAdminUser()) {
        // Admin: usa axiosInstance (JWT token automatico)
        response = await axiosInstance.get(`/voucher-rimborso/${id}`);
      } else {
        // Associato: usa axios con OTP token
        response = await axios.get(
          `${API_BASE_URL}/api/voucher-rimborso/${id}`,
          { headers: getAuthHeader() }
        );
      }

      setVoucher(response.data.data);
    } catch (error: any) {
      console.error('Errore caricamento voucher:', error);
      toast.error(error.response?.data?.message || 'Errore durante il caricamento');
      navigate('/area-riservata/rimborso-voucher');
    } finally {
      setLoading(false);
    }
  };

  const loadMessaggi = async () => {
    try {
      let response;

      if (isAdminUser()) {
        // Admin: usa axiosInstance (JWT token automatico)
        response = await axiosInstance.get(`/voucher-rimborso/${id}/messaggi`);
      } else {
        // Associato: usa axios con OTP token
        response = await axios.get(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/messaggi`,
          { headers: getAuthHeader() }
        );
      }

      setMessaggi(response.data.data);
    } catch (error: any) {
      console.error('Errore caricamento messaggi:', error);
    }
  };

  const handlePrenota = async () => {
    try {
      if (isAdminUser()) {
        await axiosInstance.post(`/voucher-rimborso/${id}/prenota`, {});
      } else {
        await axios.post(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/prenota`,
          {},
          { headers: getAuthHeader() }
        );
      }
      toast.success('Voucher prenotato con successo!');
      loadVoucher();
    } catch (error: any) {
      console.error('Errore prenotazione:', error);
      toast.error(error.response?.data?.message || 'Errore durante la prenotazione');
    }
  };

  const handleConferma = async () => {
    try {
      const body = confermaSenzaRicevuta ? { confermaSenzaRicevuta: true } : {};
      if (isAdminUser()) {
        await axiosInstance.post(`/voucher-rimborso/${id}/conferma`, body);
      } else {
        await axios.post(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/conferma`,
          body,
          { headers: getAuthHeader() }
        );
      }
      toast.success('Conferma registrata!');
      setShowConfirmModal(false);
      setConfermaSenzaRicevuta(false);
      loadVoucher();
    } catch (error: any) {
      console.error('Errore conferma:', error);
      toast.error(error.response?.data?.message || 'Errore durante la conferma');
    }
  };

  // Conferma admin per conto di un associato
  const handleAdminConferma = async (ruolo: 'proprietario' | 'prenotante') => {
    try {
      await axiosInstance.post(`/voucher-rimborso/${id}/admin-conferma`, { ruolo });
      toast.success(`Conferma registrata per ${ruolo}!`);
      loadVoucher();
    } catch (error: any) {
      console.error('Errore conferma admin:', error);
      toast.error(error.response?.data?.message || 'Errore durante la conferma');
    }
  };

  const handleAnnulla = async () => {
    if (!motivoAnnullamento.trim()) {
      toast.error('Inserisci il motivo dell\'annullamento');
      return;
    }

    try {
      if (isAdminUser()) {
        await axiosInstance.post(`/voucher-rimborso/${id}/annulla`, { motivo: motivoAnnullamento });
      } else {
        await axios.post(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/annulla`,
          { motivo: motivoAnnullamento },
          { headers: getAuthHeader() }
        );
      }
      toast.success('Voucher annullato');
      setShowAnnullaModal(false);
      navigate('/area-riservata/rimborso-voucher');
    } catch (error: any) {
      console.error('Errore annullamento:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'annullamento');
    }
  };

  const handleInviaMessaggio = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nuovoMessaggio.trim()) {
      return;
    }

    // Usa Socket.IO se connesso, altrimenti fallback a REST API
    if (socketConnected) {
      const success = sendSocketMessage(nuovoMessaggio.trim());
      if (success) {
        setNuovoMessaggio('');
        // Il messaggio arriverà via socket event 'voucher_new_message'
      } else {
        toast.error('Errore durante l\'invio del messaggio');
      }
      return;
    }

    // Fallback: usa REST API se socket non connesso
    try {
      setSendingMessage(true);

      if (isAdminUser()) {
        // Admin: invia messaggio broadcast (senza destinatario specifico)
        await axiosInstance.post(
          `/voucher-rimborso/${id}/messaggi`,
          { testo: nuovoMessaggio.trim() }
        );
      } else {
        // Associato: usa axios con OTP token
        await axios.post(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/messaggi`,
          { testo: nuovoMessaggio.trim() },
          { headers: getAuthHeader() }
        );
      }

      setNuovoMessaggio('');
      loadMessaggi();
      toast.success('Messaggio inviato');
    } catch (error: any) {
      console.error('Errore invio messaggio:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'invio del messaggio');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handler per typing indicator
  // L'admin deve essere "in chat" per mostrare il typing agli associati
  const handleTyping = () => {
    if (socketConnected) {
      // Se è admin, invia typing solo se è entrato in chat
      if (isAdminUser() && !voucher?.adminInChat) {
        return;
      }
      startTyping();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- handler conservato: flusso intervento admin non esposto in UI ma riusabile
  const handleRichiediInterventoAdmin = async () => {
    try {
      setRichiedendoIntervento(true);

      await axios.post(
        `${API_BASE_URL}/api/voucher-rimborso/${id}/richiedi-intervento-admin`,
        {},
        { headers: getAuthHeader() }
      );

      toast.success('Richiesta di intervento amministrativo inviata');
      loadVoucher(); // Ricarica voucher per aggiornare lo stato
    } catch (error: any) {
      console.error('Errore richiesta intervento:', error);
      toast.error(error.response?.data?.message || 'Errore durante la richiesta di intervento');
    } finally {
      setRichiedendoIntervento(false);
    }
  };

  const handleAdminEntraChat = async () => {
    try {
      setEntrandoInChat(true);

      await axiosInstance.post(`/voucher-rimborso/${id}/admin-entra-chat`);

      toast.success('Sei entrato nella chat');
      loadVoucher(); // Ricarica voucher per aggiornare lo stato
    } catch (error: any) {
      console.error('Errore ingresso chat:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'ingresso in chat');
    } finally {
      setEntrandoInChat(false);
    }
  };

  const handleAdminLasciaChat = async () => {
    try {
      setUscendoDaChat(true);

      await axiosInstance.post(`/voucher-rimborso/${id}/admin-lascia-chat`);

      toast.success('Sei uscito dalla chat');
      loadVoucher(); // Ricarica voucher per aggiornare lo stato
    } catch (error: any) {
      console.error('Errore uscita chat:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'uscita dalla chat');
    } finally {
      setUscendoDaChat(false);
    }
  };

  const handleDownloadVoucher = async () => {
    try {
      let response;

      if (isAdminUser()) {
        // Admin: usa axiosInstance (JWT token automatico)
        response = await axiosInstance.get(`/voucher-rimborso/${id}/download-voucher`, {
          responseType: 'blob'
        });
      } else {
        // Associato: usa axios con OTP token
        const authHeader = getAuthHeader() as { Authorization?: string };
        const token = authHeader.Authorization?.replace('Bearer ', '') || '';
        response = await axios.get(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/download-voucher`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
      }

      // Crea un URL temporaneo per il blob e scarica il file
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = voucher?.voucherPDF?.filename || 'voucher.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF scaricato con successo');
    } catch (error: any) {
      console.error('Errore download voucher:', error);
      toast.error(error.response?.data?.message || 'Errore durante il download del PDF');
    }
  };

  const handleDownloadRicevuta = async () => {
    try {
      let response;

      if (isAdminUser()) {
        // Admin: usa axiosInstance (JWT token automatico)
        response = await axiosInstance.get(`/voucher-rimborso/${id}/download-ricevuta`, {
          responseType: 'blob'
        });
      } else {
        // Associato: usa axios con OTP token
        const authHeader = getAuthHeader() as { Authorization?: string };
        const token = authHeader.Authorization?.replace('Bearer ', '') || '';
        response = await axios.get(
          `${API_BASE_URL}/api/voucher-rimborso/${id}/download-ricevuta`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
      }

      // Crea un URL temporaneo per il blob e scarica il file
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = voucher?.ricevutaPagamentoPDF?.filename || 'ricevuta.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF scaricato con successo');
    } catch (error: any) {
      console.error('Errore download ricevuta:', error);
      toast.error(error.response?.data?.message || 'Errore durante il download del PDF');
    }
  };

  const getTariffarioLabel = (tariffario: string) => {
    const map: { [key: string]: string } = {
      'NAZIONALE': 'Nazionale',
      'TRENTO': 'Provincia Autonoma di Trento',
      'BOLZANO': 'Provincia Autonoma di Bolzano',
      'SICILIA': 'Regione Sicilia',
      'AOSTA': 'Valle d\'Aosta',
      'FRIULI_VENEZIA_GIULIA': 'Friuli Venezia Giulia'
    };
    return map[tariffario] || tariffario;
  };

  const tariffeLabels: Record<string, string> = {
    diritti: 'Diritti', bolli1: 'Bolli', bolli2: 'Bolli 2', costo: 'Costo Targa',
    gas: 'Gas Compressi', contributi: 'Contributi', diritti2: 'Diritti Stampato',
    diritti3: 'Diritti Esami', diritti4: 'Diritti Idoneità', dirittiSted: 'Diritti STED',
    corrispettivoSted: 'Corrispettivo STED', tributiSpeciali: 'Tributi Speciali',
    rimborsoMissioni: 'Rimborso Missioni',
  };

  const getImportiPrincipali = (tariffe?: Voucher['tariffe']) => {
    if (!tariffe) return null;

    const importi: string[] = [];

    const formatImporto = (valore: any) => {
      const num = typeof valore === 'string' ? parseFloat(valore) : valore;
      return !isNaN(num) && num > 0 ? num.toFixed(2) : null;
    };

    for (const [key, label] of Object.entries(tariffeLabels)) {
      const val = formatImporto((tariffe as any)[key]);
      if (val) importi.push(`${label}: €${val}`);
    }

    return importi.length > 0 ? importi.join(' • ') : null;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- helper di stato conservato per il riuso nelle viste di dettaglio
  const getStatoColor = (stato: string) => {
    const colors: { [key: string]: string } = {
      'disponibile': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      'prenotato': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      'completato': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400',
      'annullato': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      'scaduto': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
    };
    return colors[stato] || 'bg-gray-100 text-gray-800';
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- helper di stato conservato per il riuso nelle viste di dettaglio
  const getStatoLabel = (stato: string) => {
    const labels: { [key: string]: string } = {
      'disponibile': 'Disponibile',
      'prenotato': 'Prenotato',
      'completato': 'Completato',
      'annullato': 'Annullato',
      'scaduto': 'Scaduto'
    };
    return labels[stato] || stato;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Funzione upload PDF voucher (proprietario)
  const handleUploadVoucher = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Solo file PDF sono permessi');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Il file non può superare i 10MB');
      return;
    }

    setUploadingVoucher(true);

    try {
      const formData = new FormData();
      formData.append('voucher', file);

      let token;
      let url;

      if (isAdminUser()) {
        // Admin: usa JWT token
        token = localStorage.getItem('accessToken');
        url = `${API_BASE_URL}/api/voucher-rimborso/${id}/upload-voucher`;
      } else {
        // Associato: usa OTP token
        token = getToken();
        url = `${API_BASE_URL}/api/voucher-rimborso/${id}/upload-voucher`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('PDF voucher caricato con successo');
        loadVoucher(); // Ricarica i dati
      } else {
        toast.error(data.message || 'Errore durante il caricamento');
      }
    } catch (error) {
      console.error('Errore upload voucher:', error);
      toast.error('Errore durante il caricamento del PDF');
    } finally {
      setUploadingVoucher(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Funzione upload PDF ricevuta (prenotante)
  const handleUploadRicevuta = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Solo file PDF sono permessi');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Il file non può superare i 10MB');
      return;
    }

    setUploadingRicevuta(true);

    try {
      const formData = new FormData();
      formData.append('ricevuta', file);

      let token;
      let url;

      if (isAdminUser()) {
        // Admin: usa JWT token
        token = localStorage.getItem('accessToken');
        url = `${API_BASE_URL}/api/voucher-rimborso/${id}/upload-ricevuta`;
      } else {
        // Associato: usa OTP token
        token = getToken();
        url = `${API_BASE_URL}/api/voucher-rimborso/${id}/upload-ricevuta`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('PDF ricevuta caricato con successo');
        loadVoucher(); // Ricarica i dati
      } else {
        toast.error(data.message || 'Errore durante il caricamento');
      }
    } catch (error) {
      console.error('Errore upload ricevuta:', error);
      toast.error('Errore durante il caricamento del PDF');
    } finally {
      setUploadingRicevuta(false);
      // Reset input
      event.target.value = '';
    }
  };

  const isProprietario = voucher?.proprietario._id === currentAssociateId;
  const isPrenotante = voucher?.prenotante?._id === currentAssociateId;
  const canPrenota = voucher?.stato === 'disponibile' && !isProprietario;

  // Conferma legata ai documenti (utils/voucherConferma.ts, speculare al backend):
  // il prenotante conferma dopo il PDF voucher, il proprietario dopo la ricevuta
  // (o dichiarando di aver ricevuto il rimborso in altro modo).
  const esitoConferma = voucher && (isProprietario || isPrenotante)
    ? valutaConfermaScambio(isProprietario ? 'proprietario' : 'prenotante', {
        stato: voucher.stato,
        voucherPDF: !!voucher.voucherPDF,
        ricevutaPDF: !!voucher.ricevutaPagamentoPDF,
        tuHaConfermato: !!(isProprietario ? voucher.conferme?.proprietarioConfermato : voucher.conferme?.prenotatoConfermato),
        controparteHaConfermato: !!(isProprietario ? voucher.conferme?.prenotatoConfermato : voucher.conferme?.proprietarioConfermato),
        scadenza: voucher.scadenzaPrenotazione
      })
    : null;
  const canConferma = !!esitoConferma?.puoConfermare;

  const canAnnulla = isProprietario && (voucher?.stato === 'disponibile' || voucher?.stato === 'prenotato') && !voucher?.conferme?.proprietarioConfermato;
  // Messaggistica aperta se il backend dice che è aperta (15 giorni dopo completamento)
  // Admin possono sempre vedere i messaggi se il voucher è prenotato o completato
  const canMessage = isAdminUser()
    ? (voucher?.stato === 'prenotato' || voucher?.stato === 'completato')
    : (voucher?.isMessaggisticaAperta && (isProprietario || isPrenotante));

  // Controlla se l'altra parte ha già confermato
  const altraParteHaConfermato = isProprietario
    ? voucher?.conferme?.prenotatoConfermato
    : voucher?.conferme?.proprietarioConfermato;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!voucher) {
    return null;
  }

  // Helper: calcola il totale importi
  const getTotaleImporti = (tariffe?: Voucher['tariffe']) => {
    if (!tariffe) return 0;
    let totale = 0;
    const addIfValid = (val: any) => { const n = typeof val === 'string' ? parseFloat(val) : val; if (!isNaN(n) && n > 0) totale += n; };
    addIfValid(tariffe.diritti); addIfValid(tariffe.diritti2); addIfValid(tariffe.diritti3); addIfValid(tariffe.diritti4);
    addIfValid(tariffe.bolli1); addIfValid(tariffe.bolli2);
    addIfValid(tariffe.contributi); addIfValid(tariffe.costo); addIfValid(tariffe.gas);
    addIfValid(tariffe.dirittiSted); addIfValid(tariffe.corrispettivoSted); addIfValid(tariffe.tributiSpeciali); addIfValid(tariffe.rimborsoMissioni);
    return totale;
  };

  // Determina "tu" e "controparte"
  const tuData = isProprietario ? voucher?.proprietario : voucher?.prenotante;
  const controparteData = isProprietario ? voucher?.prenotante : voucher?.proprietario;
  const tuLabel = isProprietario ? 'Proprietario' : 'Prenotante';
  const controparteLabel = isProprietario ? 'Prenotante' : 'Proprietario';
  const tuHaConfermato = isProprietario ? voucher?.conferme?.proprietarioConfermato : voucher?.conferme?.prenotatoConfermato;
  const controparteHaConfermato = isProprietario ? voucher?.conferme?.prenotatoConfermato : voucher?.conferme?.proprietarioConfermato;

  // Step dello stepper
  // Step 1: Prenotato
  // Step 2a: PDF Voucher caricato
  // Step 2b: PDF Ricevuta caricato
  // Step 3: Conferme (almeno una parte ha confermato)
  // Step 4: Completato (entrambe le parti hanno confermato)
  const getStepperState = () => {
    if (voucher?.stato === 'completato') return 4; // Completato
    if (voucher?.stato === 'prenotato') {
      // Se entrambe le parti hanno confermato, passa a step 4 (completato)
      if (voucher.conferme?.proprietarioConfermato && voucher.conferme?.prenotatoConfermato) return 4;
      // Se almeno una parte ha confermato, siamo allo step 3 (conferme)
      if (voucher.conferme?.proprietarioConfermato || voucher.conferme?.prenotatoConfermato) return 3;
      // Se entrambi i PDF sono caricati, siamo allo step 2b
      if (voucher.voucherPDF && voucher.ricevutaPagamentoPDF) return 2;
      // Se solo il PDF voucher è caricato, siamo allo step 1
      if (voucher.voucherPDF) return 1;
      // Altrimenti siamo allo step 0 (prenotato)
      return 0;
    }
    if (voucher?.stato === 'annullato' || voucher?.stato === 'scaduto') return -1;
    return 0;
  };
  const stepperState = getStepperState();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
        {/* Header compatto */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/area-riservata/rimborso-voucher')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Torna ai Voucher
          </button>

          <div className="flex items-center gap-3">
            {!isAdminUser() && canAnnulla && (
              <button
                onClick={() => setShowAnnullaModal(true)}
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium underline underline-offset-2 transition-colors"
              >
                Annulla prenotazione
              </button>
            )}
            <button
              onClick={startTour}
              className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
              title="Guida"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ===== 1. STATO OPERAZIONE (Stepper) ===== */}
        <div id="tour-vdettaglio-stato" className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">Stato della Prenotazione</h2>

          {voucher.stato !== 'annullato' && voucher.stato !== 'scaduto' ? (
            <>
              {/* Stepper orizzontale con step documenti */}
              <div className="flex items-center justify-between mb-5 overflow-x-auto pb-2">
                {/* Step 1: Prenotato */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    stepperState >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}>
                    {stepperState >= 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
                  </div>
                  <span className={`text-xs mt-1.5 font-medium text-center ${stepperState >= 1 ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400'}`}>Prenotato</span>
                </div>
                {/* Linea */}
                <div className={`flex-1 h-0.5 mx-1 min-w-[20px] ${stepperState >= 2 ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-600'}`} />

                {/* Step 2a: PDF Proprietario */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                    voucher?.voucherPDF ? 'bg-green-600 text-white' : stepperState >= 2 ? 'bg-amber-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}>
                    {voucher?.voucherPDF ? <CheckCircle className="w-4 h-4" /> : '2a'}
                  </div>
                  <span className={`text-xs mt-1.5 font-medium text-center max-w-[60px] ${voucher?.voucherPDF ? 'text-green-700 dark:text-green-400' : stepperState >= 2 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>PDF Voucher</span>
                </div>
                {/* Linea */}
                <div className={`flex-1 h-0.5 mx-1 min-w-[20px] ${voucher?.voucherPDF && voucher?.ricevutaPagamentoPDF ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-600'}`} />

                {/* Step 2b: PDF Ricevuta */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                    voucher?.ricevutaPagamentoPDF ? 'bg-green-600 text-white' : stepperState >= 2 ? 'bg-amber-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}>
                    {voucher?.ricevutaPagamentoPDF ? <CheckCircle className="w-4 h-4" /> : '2b'}
                  </div>
                  <span className={`text-xs mt-1.5 font-medium text-center max-w-[60px] ${voucher?.ricevutaPagamentoPDF ? 'text-green-700 dark:text-green-400' : stepperState >= 2 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>PDF Ricevuta</span>
                </div>
                {/* Linea */}
                <div className={`flex-1 h-0.5 mx-1 min-w-[20px] ${stepperState >= 3 ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-600'}`} />

                {/* Step 3: Conferme */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    stepperState >= 4 ? 'bg-green-600 text-white' : stepperState >= 3 ? 'bg-amber-500 text-white' : stepperState === 2 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 ring-2 ring-amber-400' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}>
                    {stepperState >= 4 ? <CheckCircle className="w-5 h-5" /> : '3'}
                  </div>
                  <span className={`text-xs mt-1.5 font-medium text-center ${stepperState >= 4 ? 'text-green-700 dark:text-green-400' : stepperState >= 2 && stepperState < 4 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>Conferme</span>
                </div>
                {/* Linea */}
                <div className={`flex-1 h-0.5 mx-1 min-w-[20px] ${stepperState >= 4 ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-600'}`} />

                {/* Step 4: Completato */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    stepperState >= 4 ? 'bg-green-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}>
                    {stepperState >= 4 ? <CheckCircle className="w-5 h-5" /> : '4'}
                  </div>
                  <span className={`text-xs mt-1.5 font-medium text-center ${stepperState >= 4 ? 'text-green-700 dark:text-green-400' : 'text-gray-400'}`}>Completato</span>
                </div>
              </div>

              {/* Banner contestuale: cosa devi fare ORA */}
              {voucher.stato === 'prenotato' && (
                <>
                  {/* Se entrambe le parti hanno confermato, mostra messaggio di completamento */}
                  {voucher.conferme?.proprietarioConfermato && voucher.conferme?.prenotatoConfermato ? (
                    <div className="rounded-lg p-3.5 bg-green-50 dark:bg-green-900/15 border border-green-300 dark:border-green-700">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        ✅ Scambio completato con successo!
                      </p>
                    </div>
                  ) : (
                    <div className={`rounded-lg p-3.5 ${
                      esitoConferma?.bannerTono === 'azione'
                        ? 'bg-amber-50 dark:bg-amber-900/15 border border-amber-300 dark:border-amber-700'
                        : 'bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-700'
                    }`}>
                      <p className={`text-sm font-medium ${esitoConferma?.bannerTono === 'azione' ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'}`}>
                        {esitoConferma
                          ? esitoConferma.banner
                          : !voucher?.voucherPDF || !voucher?.ricevutaPagamentoPDF
                            ? `📄 In attesa dei documenti${voucher.scadenzaPrenotazione ? ` — scadenza: ${new Date(voucher.scadenzaPrenotazione).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}`
                            : `⏳ In attesa di conferma da entrambe le parti${voucher.scadenzaPrenotazione ? ` — scadenza: ${new Date(voucher.scadenzaPrenotazione).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}`
                        }
                      </p>
                      {!isAdminUser() && canConferma && (
                        <div className="mt-3">
                          <button
                            onClick={() => { setConfermaSenzaRicevuta(false); setShowConfirmModal(true); }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Conferma Scambio
                          </button>
                        </div>
                      )}
                      {!isAdminUser() && esitoConferma?.puoConfermareSenzaRicevuta && (
                        <p className="mt-3 text-xs text-blue-700 dark:text-blue-300">
                          Hai già ricevuto il rimborso in altro modo (es. altro voucher di pari importo)?{' '}
                          <button
                            type="button"
                            onClick={() => { setConfermaSenzaRicevuta(true); setShowConfirmModal(true); }}
                            className="font-semibold underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100"
                          >
                            Conferma comunque
                          </button>
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              {voucher.stato === 'completato' && (
                <div className="rounded-lg p-3.5 bg-green-50 dark:bg-green-900/15 border border-green-300 dark:border-green-700">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    ✅ Scambio completato con successo{voucher.dataCompletamento ? ` il ${new Date(voucher.dataCompletamento).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
                  </p>
                </div>
              )}
              {voucher.stato === 'disponibile' && (
                <div className="rounded-lg p-3.5 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    📋 Questo voucher è disponibile per la prenotazione
                  </p>
                </div>
              )}
            </>
          ) : (
            /* Stato annullato/scaduto */
            <div className={`rounded-lg p-3.5 ${
              voucher.stato === 'annullato'
                ? 'bg-red-50 dark:bg-red-900/15 border border-red-300 dark:border-red-700'
                : 'bg-orange-50 dark:bg-orange-900/15 border border-orange-300 dark:border-orange-700'
            }`}>
              <p className={`text-sm font-medium ${voucher.stato === 'annullato' ? 'text-red-800 dark:text-red-200' : 'text-orange-800 dark:text-orange-200'}`}>
                {voucher.stato === 'annullato' ? '❌ Voucher annullato' : '⏰ Prenotazione scaduta'}
                {voucher.motivoAnnullamento && ` — ${voucher.motivoAnnullamento}`}
              </p>
            </div>
          )}
        </div>

        {/* ===== 2. AZIONE RICHIESTA (CTA contestuale) ===== */}
        {(canPrenota || isAdminUser()) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
            {/* Layout: Prenota a sinistra */}
            <div className="flex flex-wrap items-center gap-3">
              {canPrenota && (
                <button
                  onClick={handlePrenota}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <Ticket className="w-5 h-5" />
                  Prenota Voucher
                </button>
              )}

              {/* Azioni Admin */}
              {isAdminUser() && voucher.stato === 'prenotato' && (
                <div className="flex flex-wrap items-center gap-2 ml-auto pl-4 border-l border-gray-200 dark:border-gray-600">
                  <span className="text-xs text-gray-400 uppercase font-semibold mr-1">Admin:</span>
                  {!voucher.conferme?.proprietarioConfermato && (
                    <button
                      onClick={() => handleAdminConferma('proprietario')}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Conferma Proprietario
                    </button>
                  )}
                  {!voucher.conferme?.prenotatoConfermato && (
                    <button
                      onClick={() => handleAdminConferma('prenotante')}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Conferma Prenotante
                    </button>
                  )}
                  <button
                    onClick={() => setShowAnnullaModal(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Annulla
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== 3. LE PARTI COINVOLTE (Tu vs Controparte) ===== */}
        {voucher.prenotante && (isProprietario || isPrenotante || isAdminUser()) && (
          <div id="tour-vdettaglio-contatti" className="mb-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Le Parti Coinvolte</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
              {/* Icona scambio al centro */}
              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-full items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-gray-400" />
              </div>

              {/* Card TU */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">
                  {isAdminUser() ? 'Proprietario' : 'Tu'} <span className="text-gray-400 font-normal normal-case">({tuLabel})</span>
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{tuData?.nominativo}</span>
                </div>
                {tuData?.citta && tuData?.provincia && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{tuData.citta} ({tuData.provincia})</p>
                )}
                {/* Badge stato conferma */}
                {voucher.stato === 'prenotato' && (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    tuHaConfermato
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  }`}>
                    {tuHaConfermato ? <><CheckCircle className="w-3 h-3" /> Confermato</> : <><Clock className="w-3 h-3" /> {isAdminUser() ? 'Deve confermare' : (esitoConferma?.badge || 'Devi confermare')}</>}
                  </span>
                )}
                {/* Accordion dettagli */}
                <button
                  onClick={() => setShowDettagliTu(!showDettagliTu)}
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mt-3"
                >
                  {showDettagliTu ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showDettagliTu ? 'Nascondi dettagli' : 'Mostra dettagli'}
                </button>
                {showDettagliTu && tuData && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-0.5 text-xs [&_p]:text-xs [&_p]:leading-tight text-gray-500 dark:text-gray-400">
                    {tuData.rappresentante && <p>Legale Rapp.: <span className="text-gray-700 dark:text-gray-300">{tuData.rappresentante}</span></p>}
                    <p>Codice: <span className="text-gray-700 dark:text-gray-300">{tuData.codice}</span></p>
                    <p>Email: <span className="text-gray-700 dark:text-gray-300">{tuData.email}</span></p>
                    {tuData.telefono && <p>Tel: <span className="text-gray-700 dark:text-gray-300">{tuData.telefono}</span></p>}
                    {tuData.staCodes?.map((sta) => (
                      <div key={sta._id}>
                        <p>STA: <span className="font-medium text-gray-800 dark:text-gray-200">{sta.staCode}</span></p>
                        {sta.matricole?.length > 0 && <p className="ml-2">Matricole: <span className="text-gray-700 dark:text-gray-300">{sta.matricole.map(m => m.code).join(', ')}</span></p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Card CONTROPARTE */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-2">
                  {isAdminUser() ? 'Prenotante' : 'Controparte'} <span className="text-gray-400 font-normal normal-case">({controparteLabel})</span>
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{controparteData?.nominativo}</span>
                </div>
                {controparteData?.citta && controparteData?.provincia && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{controparteData.citta} ({controparteData.provincia})</p>
                )}
                {/* Badge stato conferma */}
                {voucher.stato === 'prenotato' && (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    controparteHaConfermato
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-600/30 text-gray-600 dark:text-gray-300'
                  }`}>
                    {controparteHaConfermato ? <><CheckCircle className="w-3 h-3" /> Confermato</> : <><Clock className="w-3 h-3" /> In attesa</>}
                  </span>
                )}
                {/* Accordion dettagli */}
                <button
                  onClick={() => setShowDettagliControparte(!showDettagliControparte)}
                  className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline mt-3"
                >
                  {showDettagliControparte ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showDettagliControparte ? 'Nascondi dettagli' : 'Mostra dettagli'}
                </button>
                {showDettagliControparte && controparteData && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-0.5 text-xs [&_p]:text-xs [&_p]:leading-tight text-gray-500 dark:text-gray-400">
                    {controparteData.rappresentante && <p>Legale Rapp.: <span className="text-gray-700 dark:text-gray-300">{controparteData.rappresentante}</span></p>}
                    <p>Codice: <span className="text-gray-700 dark:text-gray-300">{controparteData.codice}</span></p>
                    <p>Email: <span className="text-gray-700 dark:text-gray-300">{controparteData.email}</span></p>
                    {controparteData.telefono && <p>Tel: <span className="text-gray-700 dark:text-gray-300">{controparteData.telefono}</span></p>}
                    {controparteData.staCodes?.map((sta) => (
                      <div key={sta._id}>
                        <p>STA: <span className="font-medium text-gray-800 dark:text-gray-200">{sta.staCode}</span></p>
                        {sta.matricole?.length > 0 && <p className="ml-2">Matricole: <span className="text-gray-700 dark:text-gray-300">{sta.matricole.map(m => m.code).join(', ')}</span></p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Proprietario solo (quando non c'è prenotante o utente non coinvolto) */}
        {(!voucher.prenotante || (!isProprietario && !isPrenotante && !isAdminUser())) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Proprietario</h2>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{voucher.proprietario.nominativo}</span>
            </div>
            {voucher.proprietario.citta && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{voucher.proprietario.citta}{voucher.proprietario.provincia ? ` (${voucher.proprietario.provincia})` : ''}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Codice: {voucher.proprietario.codice}</p>
          </div>
        )}

        {/* ===== 4. DETTAGLI VOUCHER ===== */}
        <div id="tour-vdettaglio-pratica" className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Ticket className="w-4 h-4 text-gray-400" />
            Dettagli Voucher
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                {voucher.pratica.descrizionePratica}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {voucher.pratica.categoria} • {voucher.pratica.catalogo}
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-gray-600 dark:text-gray-400">
                <span>Codice: <strong className="text-gray-900 dark:text-white">{voucher.pratica.codicePratica}</strong></span>
                <span>Tariffario: <strong className="text-gray-900 dark:text-white">{getTariffarioLabel(voucher.tariffario)}</strong></span>
                <span>Quantità: <strong className="text-gray-900 dark:text-white">{voucher.quantita}</strong></span>
                <span>Causale: <strong className="text-gray-900 dark:text-white">{voucher.causale}</strong></span>
                {voucher.isCumulativo && (
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded">
                    ⚡ Cumulativo: {voucher.numeroPratiche} pratiche
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Creato il {formatDate(voucher.createdAt)}
              </p>
              {voucher.note && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">Note: {voucher.note}</p>
              )}
            </div>
            {/* Importi */}
            {getImportiPrincipali(voucher.tariffe) && (
              <div className="sm:text-right shrink-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{getImportiPrincipali(voucher.tariffe)}</p>
                {getTotaleImporti(voucher.tariffe) > 0 && (
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    Totale: €{(getTotaleImporti(voucher.tariffe) * (voucher.quantita || 1)).toFixed(2)}
                  </p>
                )}
                {voucher.quantita > 1 && getTotaleImporti(voucher.tariffe) > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    ({voucher.quantita} × €{getTotaleImporti(voucher.tariffe).toFixed(2)})
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ===== 5. DOCUMENTI OPERAZIONE ===== */}
        {(isProprietario || isPrenotante || isAdminUser()) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Documenti Operazione
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Voucher PagoPA (proprietario) */}
              <div id="tour-vdettaglio-upload-voucher" className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-600">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Voucher PagoPA</p>
                {voucher.voucherPDF ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Caricato il {formatDate(voucher.voucherPDF.uploadedAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleDownloadVoucher}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Scarica PDF
                      </button>
                      {isProprietario && (
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors cursor-pointer text-xs font-medium">
                          <Upload className="w-3.5 h-3.5" />
                          Sostituisci
                          <input type="file" accept="application/pdf" onChange={handleUploadVoucher} className="hidden" disabled={uploadingVoucher} />
                        </label>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    {isProprietario ? (
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-xs font-medium">
                        <Upload className="w-3.5 h-3.5" />
                        {uploadingVoucher ? 'Caricamento...' : 'Carica PDF Voucher'}
                        <input type="file" accept="application/pdf" onChange={handleUploadVoucher} className="hidden" disabled={uploadingVoucher} />
                      </label>
                    ) : (
                      <p className="text-xs text-gray-400 italic">
                        {isAdminUser() ? 'Non ancora caricato dal proprietario' : 'In attesa del caricamento da parte del proprietario'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Ricevuta Pagamento (prenotante) */}
              <div id="tour-vdettaglio-upload-ricevuta" className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-600">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Ricevuta Pagamento</p>
                {voucher.ricevutaPagamentoPDF ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Caricato il {formatDate(voucher.ricevutaPagamentoPDF.uploadedAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleDownloadRicevuta}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Scarica PDF
                      </button>
                      {isPrenotante && (
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors cursor-pointer text-xs font-medium">
                          <Upload className="w-3.5 h-3.5" />
                          Sostituisci
                          <input type="file" accept="application/pdf" onChange={handleUploadRicevuta} className="hidden" disabled={uploadingRicevuta} />
                        </label>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    {isPrenotante ? (
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-xs font-medium">
                        <Upload className="w-3.5 h-3.5" />
                        {uploadingRicevuta ? 'Caricamento...' : 'Carica PDF Ricevuta'}
                        <input type="file" accept="application/pdf" onChange={handleUploadRicevuta} className="hidden" disabled={uploadingRicevuta} />
                      </label>
                    ) : (
                      <p className="text-xs text-gray-400 italic">
                        {isAdminUser() ? 'Non ancora caricato dal prenotante' : 'In attesa del caricamento da parte del prenotante'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== 6. COMUNICAZIONI ===== */}
        {canMessage && (
          <div id="tour-vdettaglio-messaggi" className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-gray-400" />
                Comunicazioni
                {socketEnabled && (
                  <span
                    className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                      socketConnected
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {socketConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                    {socketConnected ? 'Live' : '...'}
                  </span>
                )}
              </h2>
              {/* Admin chat controls */}
              {isAdminUser() && (
                <div className="flex items-center gap-2">
                  {voucher.adminInChat ? (
                    <button onClick={handleAdminLasciaChat} disabled={uscendoDaChat}
                      className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs font-medium rounded-full border border-red-300 dark:border-red-700 hover:bg-red-200 transition-colors disabled:opacity-50">
                      {uscendoDaChat ? '...' : '🚪 Lascia Chat'}
                    </button>
                  ) : (
                    <button onClick={handleAdminEntraChat} disabled={entrandoInChat}
                      className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs font-medium rounded-full border border-orange-300 dark:border-orange-700 hover:bg-orange-200 transition-colors disabled:opacity-50">
                      {entrandoInChat ? '...' : '🔧 Entra in Chat'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Banner completamento */}
            {voucher.stato === 'completato' && (
              <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                <p className="text-xs text-emerald-700 dark:text-emerald-400">
                  ✅ Scambio completato. La messaggistica resterà aperta per 15 giorni.
                </p>
              </div>
            )}

            {/* Lista Messaggi */}
            <div ref={messagesContainerRef} className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {messaggi.length === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-6 text-sm">
                  Nessun messaggio. Inizia la conversazione!
                </p>
              ) : (
                messaggi.map((msg) => {
                  const isMine = msg.mittente?._id === currentAssociateId;
                  const isMsgAdmin = !!msg.mittenteAdmin;
                  const isSistema = msg.tipo === 'sistema';
                  if (isSistema) {
                    return (
                      <div key={msg._id} className="flex justify-center my-2">
                        <div className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs px-4 py-2 rounded-full">
                          {msg.testo}
                          <span className="ml-2 opacity-60">{formatDate(msg.createdAt)}</span>
                        </div>
                      </div>
                    );
                  }
                  const mittenteName = isMsgAdmin
                    ? `Admin: ${msg.mittenteAdminNome || 'Amministratore'}`
                    : msg.mittente?.nominativo || 'Sconosciuto';
                  return (
                    <div key={msg._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-lg p-3 ${
                        isMsgAdmin ? 'bg-orange-500 text-white border-2 border-orange-600'
                        : isMine ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}>
                        <p className={`text-xs font-medium mb-1 ${isMsgAdmin ? 'text-orange-100 opacity-90' : isMine ? 'text-blue-200 opacity-70' : 'text-gray-500 dark:text-gray-400'}`}>
                          {mittenteName}
                        </p>
                        <p className="text-sm">{msg.testo}</p>
                        <p className={`text-xs mt-1 ${isMsgAdmin ? 'text-orange-100 opacity-70' : isMine ? 'text-blue-200 opacity-60' : 'text-gray-500 dark:text-gray-400'}`}>
                          {formatDate(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Typing */}
            {typingUsers.length > 0 && (
              <div className="mb-2 text-sm text-gray-500 dark:text-gray-400 italic">
                {typingUsers.map(t => t.name).join(', ')} sta scrivendo...
              </div>
            )}
            {socketConnected && onlineUsers.filter(u => !u.isAdmin).length > 1 && (
              <div className="mb-2 text-xs text-gray-400 dark:text-gray-500">
                Online: {onlineUsers.filter(u => !u.isAdmin).map(u => u.name).join(', ')}
              </div>
            )}

            {/* Form Invio */}
            <form onSubmit={handleInviaMessaggio} className="flex gap-2">
              <input
                type="text"
                value={nuovoMessaggio}
                onChange={(e) => { setNuovoMessaggio(e.target.value); handleTyping(); }}
                placeholder="Scrivi un messaggio..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                disabled={sendingMessage}
              />
              <button
                type="submit"
                disabled={sendingMessage || !nuovoMessaggio.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}

        {/* Modal Conferma */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Conferma Scambio
              </h3>
              <div className="space-y-3 mb-6">
                {/* Messaggio personalizzato per proprietario */}
                {isProprietario && (
                  <p className="text-gray-600 dark:text-gray-400">
                    Confermando lo scambio, dichiari di aver ricevuto il rimborso o altro voucher di pari importo.
                  </p>
                )}
                {isProprietario && confermaSenzaRicevuta && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded p-3">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      ⚠️ Il prenotante non ha ancora caricato la ricevuta di pagamento. Conferma solo se hai
                      effettivamente già ricevuto il rimborso (o un altro voucher di pari importo).
                    </p>
                  </div>
                )}
                {/* Messaggio personalizzato per prenotante */}
                {isPrenotante && (
                  <p className="text-gray-600 dark:text-gray-400">
                    Confermando lo scambio, dichiari di aver ricevuto il voucher scelto.
                  </p>
                )}
                {altraParteHaConfermato ? (
                  <>
                    <p className="text-gray-900 dark:text-white font-medium">
                      ⚠️ L'altra parte ha già confermato. Cliccando su "Conferma" lo scambio sarà completato.
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        ℹ️ Dopo il completamento, la messaggistica resterà aperta per <strong>15 giorni</strong> per eventuali comunicazioni.
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    L'altra parte dovrà confermare per completare lo scambio.
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowConfirmModal(false); setConfermaSenzaRicevuta(false); }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleConferma}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Conferma
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Annulla */}
        {showAnnullaModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Annulla Voucher
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Sei sicuro di voler annullare questo voucher? Questa azione non può essere annullata.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo annullamento *
                </label>
                <textarea
                  value={motivoAnnullamento}
                  onChange={(e) => setMotivoAnnullamento(e.target.value)}
                  rows={3}
                  placeholder="Spiega il motivo dell'annullamento..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAnnullaModal(false);
                    setMotivoAnnullamento('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-colors"
                >
                  Chiudi
                </button>
                <button
                  onClick={handleAnnulla}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Annulla Voucher
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RimborsoVoucherDettaglioPage;

